/**
 * PBP Recommender – on-device product ranking for the SSDC hot-picks sections.
 * ---------------------------------------------------------------------------
 * Storage: localStorage key "pbp_taste_v1" => JSON array of interaction events.
 * Nothing is ever sent to a server; the profile lives only in the visitor's browser.
 *
 * How it works
 *   1. Each page records one small event: which scent notes, collections and
 *      price point the visitor just looked at (or wishlisted / added to cart).
 *   2. Events fade out over time – an event is worth half as much after
 *      HALF_LIFE_DAYS, so last week's browsing outranks last month's.
 *   3. A section renders a candidate pool (more cards than it displays). This
 *      script scores every candidate against the profile, reorders the pool and
 *      reveals the highest scoring ones.
 *
 * Degrades safely: with no history, no localStorage, or no JS at all, the
 * section keeps the merchant's manual collection order.
 */

(function () {
  'use strict';

  /* ── Weights: how much each interaction counts ────────── */
  var SIGNAL_WEIGHT = {
    view: 1, // opened a product page
    collection: 0.5, // browsed a collection page
    wishlist: 3, // saved to the wishlist
    cart: 5, // added to cart – the strongest intent
  };

  /* ── Weights: how much each kind of match counts when ranking ─── */
  var SCORE_WEIGHT = {
    scent: 3.0, // shares scent notes with what the visitor likes
    collection: 1.5, // sits in a collection the visitor keeps browsing
    price: 1.0, // priced near the visitor's usual bracket
    merchant: 0.8, // tie-breaker that respects the manual collection order
    repeat: -1.2, // already seen recently – push down rather than repeat
  };

  var HALF_LIFE_DAYS = 21; // an event loses half its weight after this long
  var MIN_STRENGTH = 0.9; // below this much history, don't reorder anything
  var MAX_EVENTS = 80; // rolling window kept in storage
  var DEDUPE_MINUTES = 30; // the same signal inside this window counts once
  var PRICE_SPREAD = 3; // price affinity reaches 0 this many times apart

  var STORAGE_KEY = 'pbp_taste_v1';
  var DAY = 86400000;

  /* ── storage helpers ─────────────────────────────────── */
  function readEvents() {
    try {
      var raw = JSON.parse(localStorage.getItem(STORAGE_KEY));
      return Array.isArray(raw) ? raw : [];
    } catch (_) {
      return [];
    }
  }

  function writeEvents(events) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(events));
    } catch (_) {
      /* private mode / quota – personalisation just stays off */
    }
  }

  function splitList(value) {
    return String(value || '')
      .split('|')
      .map(function (part) {
        return part.trim();
      })
      .filter(Boolean);
  }

  /* ── recording ───────────────────────────────────────── */
  function record(kind, data) {
    if (!SIGNAL_WEIGHT[kind] || !data) return;

    var key = kind + ':' + (data.id || data.collection || '');
    var now = Date.now();
    var events = readEvents();

    for (var i = events.length - 1; i >= 0; i--) {
      if (events[i].k === key && now - events[i].t < DEDUPE_MINUTES * 60000) return;
    }

    events.push({
      k: key,
      n: kind,
      t: now,
      s: data.scents || [],
      c: data.cols || [],
      p: data.price || 0,
      id: data.id ? String(data.id) : null,
    });

    if (events.length > MAX_EVENTS) events = events.slice(events.length - MAX_EVENTS);
    writeEvents(events);
  }

  /* ── profile ─────────────────────────────────────────── */
  function normalise(map) {
    var max = 0;
    Object.keys(map).forEach(function (key) {
      if (map[key] > max) max = map[key];
    });
    if (!max) return map;
    Object.keys(map).forEach(function (key) {
      map[key] = map[key] / max;
    });
    return map;
  }

  function buildProfile() {
    var events = readEvents();
    var now = Date.now();
    var scent = {};
    var cols = {};
    var seen = {};
    var priceSum = 0;
    var priceWeight = 0;
    var strength = 0;

    events.forEach(function (event) {
      var age = Math.max(0, now - event.t);
      var decay = Math.pow(0.5, age / (HALF_LIFE_DAYS * DAY));
      var weight = (SIGNAL_WEIGHT[event.n] || 0) * decay;
      if (weight < 0.02) return;

      strength += weight;

      (event.s || []).forEach(function (note) {
        scent[note] = (scent[note] || 0) + weight;
      });
      (event.c || []).forEach(function (handle) {
        cols[handle] = (cols[handle] || 0) + weight;
      });
      if (event.p > 0) {
        priceSum += event.p * weight;
        priceWeight += weight;
      }
      if (event.id) seen[event.id] = Math.max(seen[event.id] || 0, decay);
    });

    return {
      scent: normalise(scent),
      cols: normalise(cols),
      price: priceWeight ? priceSum / priceWeight : 0,
      seen: seen,
      strength: strength,
    };
  }

  /* ── scoring ─────────────────────────────────────────── */
  function priceAffinity(price, preferred) {
    if (!price || !preferred) return 0;
    var distance = Math.abs(Math.log(price / preferred)) / Math.log(PRICE_SPREAD);
    return distance >= 1 ? 0 : 1 - distance;
  }

  function cardAttributes(card) {
    return {
      id: card.getAttribute('data-pbp-id'),
      scents: splitList(card.getAttribute('data-pbp-scent')),
      cols: splitList(card.getAttribute('data-pbp-col')),
      price: parseInt(card.getAttribute('data-pbp-price'), 10) || 0,
    };
  }

  function scoreCard(attributes, profile, index, poolSize) {
    var scentMatch = 0;
    if (attributes.scents.length) {
      attributes.scents.forEach(function (note) {
        scentMatch += profile.scent[note] || 0;
      });
      scentMatch = scentMatch / attributes.scents.length;
    }

    var collectionMatch = 0;
    attributes.cols.forEach(function (handle) {
      collectionMatch = Math.max(collectionMatch, profile.cols[handle] || 0);
    });

    var merchantOrder = poolSize > 1 ? 1 - index / (poolSize - 1) : 1;
    var repeat = attributes.id ? profile.seen[attributes.id] || 0 : 0;

    return (
      SCORE_WEIGHT.scent * scentMatch +
      SCORE_WEIGHT.collection * collectionMatch +
      SCORE_WEIGHT.price * priceAffinity(attributes.price, profile.price) +
      SCORE_WEIGHT.merchant * merchantOrder +
      SCORE_WEIGHT.repeat * repeat
    );
  }

  /* ── ranking a section ───────────────────────────────── */
  function personalise(grid, profile) {
    var show = parseInt(grid.getAttribute('data-pbp-show'), 10) || 0;
    var cards = Array.prototype.slice.call(grid.querySelectorAll('[data-pbp-id]'));
    if (!show || cards.length <= show) return;

    var scored = cards.map(function (card, index) {
      return {
        card: card,
        index: index,
        score: scoreCard(cardAttributes(card), profile, index, cards.length),
      };
    });

    scored.sort(function (a, b) {
      return b.score - a.score || a.index - b.index;
    });

    var fragment = document.createDocumentFragment();
    scored.forEach(function (entry, position) {
      entry.card.classList.toggle('ssdc-hotpicks__card--pool', position >= show);
      fragment.appendChild(entry.card);
    });

    grid.appendChild(fragment);
    grid.setAttribute('data-pbp-personalised', 'true');
  }

  /* ── page context ────────────────────────────────────── */
  function pageContext() {
    var el = document.getElementById('pbp-page-context');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (_) {
      return null;
    }
  }

  /**
   * Resolves scent/collection/price data for a product id using whatever is on
   * the page: the page product itself, or any rendered candidate card.
   */
  function lookupProduct(productId, context) {
    var id = String(productId);
    if (context && context.product && String(context.product.id) === id) return context.product;

    var card = document.querySelector('[data-pbp-id="' + id.replace(/"/g, '') + '"]');
    return card ? cardAttributes(card) : null;
  }

  /* ── init ────────────────────────────────────────────── */
  function init() {
    var context = pageContext();

    /*
     * Rank against what the visitor did BEFORE this page, then record this page.
     * That way a first-time visitor still sees the merchant's starter picks on
     * the page they land on, and personalisation starts from their second page.
     */
    var grids = Array.prototype.slice.call(document.querySelectorAll('[data-pbp-grid]'));
    if (grids.length) {
      var profile = buildProfile();
      if (profile.strength >= MIN_STRENGTH) {
        grids.forEach(function (grid) {
          personalise(grid, profile);
        });
      }
    }

    if (context && context.product) {
      record('view', context.product);
    } else if (context && context.collection) {
      record('collection', { collection: context.collection, cols: [context.collection] });
    }

    document.addEventListener('cart:update', function (event) {
      var productId = event.detail && event.detail.data && event.detail.data.productId;
      if (!productId) return;
      var product = lookupProduct(productId, context);
      if (product) record('cart', product);
    });

    var wishlisted = {};
    document.addEventListener('pbp:wishlist:change', function (event) {
      var items = (event.detail && event.detail.items) || [];
      items.forEach(function (item) {
        var id = String(item.id);
        if (wishlisted[id]) return;
        wishlisted[id] = true;
        var product = lookupProduct(id, context);
        if (product) record('wishlist', product);
      });
    });
  }

  /* Exposed for tuning and debugging from the console. */
  window.PBPRecommender = {
    profile: buildProfile,
    events: readEvents,
    record: record,
    reset: function () {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (_) {}
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
